/**
 * BookingModal.js — Modal de reserva de voo
 *
 * Regras de passageiro:
 *   1. Lupa → abre lista completa → clica → preenche → alerta campos faltando
 *   2. Manual → digita tudo → após reservar salva automaticamente
 *   3. Passageiro incompleto → alerta campos faltando → ao reservar atualiza cadastro
 */
import React, { useState, useEffect } from 'react'
import { X, Plane, CreditCard, Loader2, CheckCircle, AlertCircle, Search } from 'lucide-react'
import { colors } from '../styles/tokens'
import BookingDetail from './BookingDetail'

const INTER = "'Inter', -apple-system, sans-serif"
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api'

const labelStyle = {
  fontSize: '10px', fontWeight: 700, color: '#64748b',
  textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '4px'
}

const inputStyle = {
  width: '100%', padding: '8px 10px', fontSize: '13px',
  border: '1px solid #e2e8f0', borderRadius: '6px',
  background: 'white', color: '#0f172a', outline: 'none',
  boxSizing: 'border-box', fontFamily: 'inherit'
}

const inputErrorStyle = {
  ...inputStyle,
  border: '1px solid #fca5a5', background: '#fff5f5'
}

// ─── PassengerSearch com lupa ─────────────────────────────────────────────────

function PassengerSearch({ onSelect }) {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState([])
  const [loading, setLoading] = useState(false)
  const [showAll, setShowAll] = useState(false)
  const [allPassengers, setAllPassengers] = useState([])
  const [loadingAll, setLoadingAll] = useState(false)

  // Busca por digitação
  useEffect(() => {
    if (showAll) return
    if (query.length < 2) { setResults([]); return }
    const t = setTimeout(async () => {
      setLoading(true)
      try {
        const params = new URLSearchParams({ q: query, ...(isPublic && contactId ? { contact_id: contactId, mask_cpf: 'true' } : {}) })
        const r = await fetch(`${API_URL}/passengers/search?${params}`)
        const d = await r.json()
        setResults(d.passengers || [])
      } catch { setResults([]) }
      setLoading(false)
    }, 300)
    return () => clearTimeout(t)
  }, [query, showAll])

  // Abrir lista completa via lupa
  const handleLupa = async () => {
    setShowAll(true)
    setLoadingAll(true)
    try {
      const params2 = new URLSearchParams({ q: '', limit: '500', ...(isPublic && contactId ? { contact_id: contactId, mask_cpf: 'true' } : {}) })
      const r = await fetch(`${API_URL}/passengers/search?${params2}`)
      const d = await r.json()
      setAllPassengers(d.passengers || [])
    } catch { setAllPassengers([]) }
    setLoadingAll(false)
  }

  const handleSelect = (p) => {
    onSelect(p)
    setQuery('')
    setResults([])
    setShowAll(false)
    setAllPassengers([])
  }

  const list = showAll ? allPassengers : results

  return (
    <div style={{ position: 'relative', marginBottom: '12px' }}>
      <div style={labelStyle}>Buscar passageiro cadastrado</div>
      <div style={{ display: 'flex', gap: '6px' }}>
        <input
          style={{ ...inputStyle, flex: 1 }}
          placeholder="Nome ou CPF..."
          value={query}
          onChange={e => { setQuery(e.target.value); setShowAll(false) }}
        />
        <button
          onClick={handleLupa}
          title="Ver todos os passageiros"
          style={{
            padding: '8px 12px', border: '1px solid #e2e8f0',
            borderRadius: '6px', background: showAll ? colors.primary : 'white',
            cursor: 'pointer', display: 'flex', alignItems: 'center'
          }}
        >
          <Search size={14} color={showAll ? 'white' : colors.primary} />
        </button>
      </div>
      {(loading || loadingAll) && (
        <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '4px' }}>Buscando...</div>
      )}
      {list.length > 0 && (
        <div style={{
          position: 'absolute', zIndex: 200, width: '100%',
          background: 'white', border: '1px solid #e2e8f0',
          borderRadius: '8px', boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
          maxHeight: '280px', overflowY: 'auto'
        }}>
          {showAll && (
            <div style={{ padding: '8px 12px', fontSize: '11px', color: '#94a3b8', borderBottom: '1px solid #f1f5f9', fontWeight: 600 }}>
              {allPassengers.length} passageiros cadastrados
            </div>
          )}
          {list.map(p => (
            <div
              key={p.id}
              onClick={() => handleSelect(p)}
              style={{
                padding: '10px 12px', cursor: 'pointer', fontSize: '13px',
                borderBottom: '1px solid #f1f5f9',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center'
              }}
              onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
              onMouseLeave={e => e.currentTarget.style.background = 'white'}
            >
              <div>
                <span style={{ fontWeight: 600, color: '#0f172a' }}>{p.first_name} {p.last_name}</span>
                {(!p.cpf || !p.birth_date) && (
                  <span style={{ marginLeft: '6px', fontSize: '10px', color: '#f59e0b', fontWeight: 700 }}>
                    ⚠ dados incompletos
                  </span>
                )}
              </div>
              <span style={{ fontSize: '11px', color: '#94a3b8' }}>
                {p.cpf || '—'} · {p.birth_date || '—'}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ─── PassengerForm ────────────────────────────────────────────────────────────

function PassengerForm({ index, type, data, onChange, showErrors }) {
  const label = type === 'adult' ? `Adulto ${index + 1}` :
                type === 'child' ? `Criança ${index + 1}` : `Bebê ${index + 1}`
  const set = (field, value) => onChange(index, { ...data, [field]: value })

  const missing = []
  if (!data.first_name) missing.push('Nome')
  if (!data.last_name) missing.push('Sobrenome')
  if (!data.birth_date) missing.push('Nascimento')
  if (!data.document_number) missing.push('CPF')

  return (
    <div style={{ border: `1px solid ${showErrors && missing.length ? '#fca5a5' : '#e2e8f0'}`, borderRadius: '8px', padding: '14px', marginBottom: '12px' }}>
      <div style={{ fontSize: '11px', fontWeight: 700, color: colors.primary, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {label}
        {showErrors && missing.length > 0 && (
          <span style={{ fontSize: '10px', color: '#dc2626', fontWeight: 600 }}>
            Faltando: {missing.join(', ')}
          </span>
        )}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
        <div>
          <div style={labelStyle}>Nome *</div>
          <input style={showErrors && !data.first_name ? inputErrorStyle : inputStyle}
            placeholder="João" value={data.first_name || ''}
            onChange={e => set('first_name', e.target.value)} />
        </div>
        <div>
          <div style={labelStyle}>Sobrenome *</div>
          <input style={showErrors && !data.last_name ? inputErrorStyle : inputStyle}
            placeholder="Silva" value={data.last_name || ''}
            onChange={e => set('last_name', e.target.value)} />
        </div>
        <div>
          <div style={labelStyle}>Nascimento *</div>
          <input type="date" style={showErrors && !data.birth_date ? inputErrorStyle : inputStyle}
            value={data.birth_date || ''}
            onChange={e => set('birth_date', e.target.value)} />
        </div>
        <div>
          <div style={labelStyle}>CPF *</div>
          <input style={showErrors && !data.document_number ? inputErrorStyle : inputStyle}
            placeholder="000.000.000-00" value={data.document_number || ''}
            onChange={e => set('document_number', e.target.value)} />
        </div>
        <div>
          <div style={labelStyle}>Sexo</div>
          <select style={{ ...inputStyle }} value={data.gender || ''}
            onChange={e => set('gender', e.target.value)}>
            <option value="">—</option>
            <option value="M">Masculino</option>
            <option value="F">Feminino</option>
          </select>
        </div>
      </div>
    </div>
  )
}

// ─── Modal principal ──────────────────────────────────────────────────────────

export default function BookingModal({
  outbound, returnFlight,
  adults, children, infants,
  origin, destination,
  departureDate, returnDate,
  onClose, onSuccess,
  isPublic = false,       // modo cliente público — esconde pagamento
  defaultPassengers = [], // passageiros pré-preenchidos
  contactId = null,       // contact_id para filtrar busca de passageiros
}) {
  const buildInitialPassengers = () => {
    const list = []
    for (let i = 0; i < adults; i++)   list.push({ type: 'adult', ...(defaultPassengers[i] || {}) })
    for (let i = 0; i < children; i++) list.push({ type: 'child' })
    for (let i = 0; i < infants; i++)  list.push({ type: 'infant' })
    return list
  }

  const [passengers, setPassengers] = useState(buildInitialPassengers)
  const [showNewPassenger, setShowNewPassenger] = useState(false)
  const [newPassenger, setNewPassenger] = useState({})
  const [savingPassenger, setSavingPassenger] = useState(false)
  const [contactEmail, setContactEmail] = useState('')
  const [contactPhone, setContactPhone] = useState('')
  const [paymentMethod, setPaymentMethod] = useState('pix')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [showErrors, setShowErrors] = useState(false)
  const [step, setStep] = useState('form')
  const [bookingResult, setBookingResult] = useState(null)
  const [showDetail, setShowDetail] = useState(false)

  const handlePassengerChange = (index, updated) => {
    setPassengers(prev => prev.map((p, i) => i === index ? updated : p))
  }

  const calcTotalPrice = () => {
    if (outbound && returnFlight) {
      const baseIda   = outbound?.price?.base || 0
      const baseVolta = returnFlight?.price?.base || 0
      const taxesIda   = outbound?.price?.taxes || 0
      const taxesVolta = returnFlight?.price?.taxes || 0
      const tarifaCombinada = baseIda + baseVolta
      const rav = tarifaCombinada > 400 ? tarifaCombinada * 0.10 : 40
      return (tarifaCombinada + taxesIda + taxesVolta + rav) * (adults || 1)
    }
    return (outbound?.price?.total || 0) * (adults || 1)
  }
  const totalPrice = calcTotalPrice()

  // Salva ou atualiza passageiro no banco após reserva
  const _savePassenger = async (p) => {
    try {
      if (p._passenger_id) {
        await fetch(`${API_URL}/passengers/${p._passenger_id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            first_name:      p.first_name,
            last_name:       p.last_name,
            birth_date:      p.birth_date,
            cpf:             p.document_number,
            gender:          p.gender,
          })
        })
      } else {
        await fetch(`${API_URL}/passengers`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            first_name:      p.first_name,
            last_name:       p.last_name,
            birth_date:      p.birth_date,
            cpf:             p.document_number,
            gender:          p.gender,
            email:           contactEmail,
            phone:           contactPhone,
          })
        })
      }
    } catch (e) {
      console.warn('[BookingModal] falha ao salvar passageiro:', e)
    }
  }

  const handleReservar = async () => {
    setError(null)
    setShowErrors(true)

    // Validação
    for (let i = 0; i < passengers.length; i++) {
      const p = passengers[i]
      if (!p.first_name || !p.last_name || !p.birth_date || !p.document_number) {
        setError(`Preencha todos os campos obrigatórios do passageiro ${i + 1}`)
        setLoading(false)
        return
      }
    }
    if (!contactEmail || !contactPhone) {
      setError('Preencha e-mail e telefone de contato')
      return
    }

    setLoading(true)
    try {
      // 1. Prebook único — usa rateToken do outbound (que já contém ida+volta no roundtrip)
      const prebookRes = await fetch(`${API_URL}/flights/prebook`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          rate_token:     outbound.rateToken,
          origin,
          destination,
          departure_date: departureDate,
          ...(returnDate && { return_date: returnDate }),
          adults,
          children,
          infants,
          total_price:    outbound.price?.total,
          airline:        outbound.outbound?.segments?.[0]?.airline,
        })
      })
      const prebookData = await prebookRes.json()
      if (!prebookRes.ok) {
        setError(prebookData.error || 'Erro no prebook')
        setLoading(false)
        return
      }
      const prebookToken = prebookData.data?.prebook_token

      // 2. Book
      const bookRes = await fetch(`${API_URL}/flights/book`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          prebook_token:  prebookToken,
          contact_email:  contactEmail,
          contact_phone:  contactPhone,
          origin,
          destination,
          departure_date: departureDate,
          return_date:    returnDate || null,
          outbound,
          return_flight:  returnFlight,
          passengers:     passengers.map(p => ({
            type:            p.type || 'adult',
            first_name:      p.first_name,
            last_name:       p.last_name,
            birth_date:      p.birth_date,
            document_type:   'cpf',
            document_number: p.document_number,
            gender:          p.gender || undefined,
          })),
          payment: { method: paymentMethod },
          notes: 'Reserva DSC Travel',
        })
      })
      const bookData = await bookRes.json()
      if (!bookRes.ok) {
        setError(bookData.error || 'Erro ao confirmar reserva')
        setLoading(false)
        return
      }

      // 3. Salva/atualiza passageiros no banco
      for (const p of passengers) {
        await _savePassenger(p)
      }

      setBookingResult(bookData.data)
      setStep('success')
      setShowDetail(true)
      if (onSuccess) onSuccess(bookData.data)

    } catch (e) {
      setError('Erro de conexão. Verifique e tente novamente.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <div style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(0,0,0,0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '20px'
      }}>
        <div style={{
          background: 'white', borderRadius: '12px',
          width: '100%', maxWidth: '580px',
          maxHeight: '90vh', overflowY: 'auto',
          boxShadow: '0 20px 60px rgba(0,0,0,0.2)', fontFamily: INTER
        }}>
          {/* Header */}
          <div style={{
            background: colors.primary, padding: '16px 20px',
            borderRadius: '12px 12px 0 0',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Plane size={16} color={colors.accent} />
              <span style={{ color: 'white', fontSize: '14px', fontWeight: 700 }}>Reservar Voo</span>
              <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: '12px' }}>{origin} ↔ {destination}</span>
            </div>
            <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.7)' }}>
              <X size={18} />
            </button>
          </div>

          <div style={{ padding: '20px' }}>

            {/* Sucesso */}
            {step === 'success' && bookingResult && (
              <div style={{ textAlign: 'center', padding: '20px 0' }}>
                <CheckCircle size={48} color={colors.accent} style={{ marginBottom: '12px' }} />
                <div style={{ fontSize: '16px', fontWeight: 700, color: colors.primary, marginBottom: '16px' }}>
                  {isPublic ? 'Reserva confirmada!' : 'Reserva criada!'}
                </div>
                {isPublic && (
                  <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '16px', padding: '0 12px' }}>
                    A DSC Travel irá confirmar e emitir seu bilhete em breve.
                  </div>
                )}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '16px', textAlign: 'left' }}>
                  {bookingResult.booking?.booking_number && (
                    <div style={{ background: '#f8fafc', borderRadius: '8px', padding: '10px' }}>
                      <div style={{ fontSize: '10px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', marginBottom: '2px' }}>Nº Reserva</div>
                      <div style={{ fontSize: '13px', fontWeight: 700, color: colors.primary }}>{bookingResult.booking.booking_number}</div>
                    </div>
                  )}
                  {bookingResult.booking?.external_locator && (
                    <div style={{ background: '#f8fafc', borderRadius: '8px', padding: '10px' }}>
                      <div style={{ fontSize: '10px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', marginBottom: '2px' }}>PNR Rextur</div>
                      <div style={{ fontSize: '13px', fontWeight: 700, color: colors.primary }}>{bookingResult.booking.external_locator}</div>
                    </div>
                  )}
                  {bookingResult.booking?.locator && (
                    <div style={{ background: '#f8fafc', borderRadius: '8px', padding: '10px' }}>
                      <div style={{ fontSize: '10px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', marginBottom: '2px' }}>Localizador</div>
                      <div style={{ fontSize: '13px', fontWeight: 700, color: colors.primary }}>{bookingResult.booking.locator}</div>
                    </div>
                  )}
                  {bookingResult.booking?.total_amount && (
                    <div style={{ background: '#f8fafc', borderRadius: '8px', padding: '10px' }}>
                      <div style={{ fontSize: '10px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', marginBottom: '2px' }}>Total</div>
                      <div style={{ fontSize: '13px', fontWeight: 700, color: colors.primary }}>
                        {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(bookingResult.booking.total_amount)}
                      </div>
                    </div>
                  )}
                </div>
                {!isPublic && bookingResult.payment?.pix_code && (
                  <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', padding: '12px', marginBottom: '16px', textAlign: 'left' }}>
                    <div style={{ fontSize: '11px', fontWeight: 700, color: '#16a34a', marginBottom: '6px' }}>PIX — COPIA E COLA</div>
                    <code style={{ fontSize: '10px', color: '#0f172a', wordBreak: 'break-all', display: 'block', lineHeight: 1.5 }}>
                      {bookingResult.payment.pix_code}
                    </code>
                  </div>
                )}
                <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                  <button onClick={() => setShowDetail(true)} style={{ padding: '10px 20px', background: colors.accent, color: colors.primary, border: 'none', borderRadius: '8px', fontWeight: 700, fontSize: '13px', cursor: 'pointer' }}>
                    Ver Detalhes
                  </button>
                  <button onClick={onClose} style={{ padding: '10px 20px', background: colors.primary, color: 'white', border: 'none', borderRadius: '8px', fontWeight: 700, fontSize: '13px', cursor: 'pointer' }}>
                    Fechar
                  </button>
                </div>
              </div>
            )}

            {/* Formulário */}
            {step === 'form' && (
              <>
                {/* Resumo */}
                <div style={{ background: '#f8fafc', borderRadius: '8px', padding: '12px 14px', marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ fontSize: '12px', color: '#64748b' }}>{adults + children + infants} passageiro{adults + children + infants > 1 ? 's' : ''}</div>
                  <div style={{ fontSize: '16px', fontWeight: 700, color: colors.primary }}>
                    {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(totalPrice)}
                  </div>
                </div>

                {/* Passageiros */}
                <div style={{ marginBottom: '20px' }}>
                  <div style={{ fontSize: '12px', fontWeight: 700, color: '#0f172a', marginBottom: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    Passageiros
                    <button onClick={() => setShowNewPassenger(!showNewPassenger)} style={{ fontSize: '11px', fontWeight: 600, color: colors.primary, background: 'none', border: `1px solid ${colors.primary}`, borderRadius: '6px', padding: '3px 10px', cursor: 'pointer' }}>
                      + Novo
                    </button>
                  </div>

                  <PassengerSearch onSelect={(p) => {
                    setPassengers(prev => {
                      const updated = [...prev]
                      const idx = updated.findIndex(x => !x.first_name)
                      const target = idx >= 0 ? idx : 0
                      updated[target] = {
                        ...updated[target],
                        _passenger_id: p.id,
                        first_name:    p.first_name,
                        last_name:     p.last_name,
                        birth_date:    p.birth_date,
                        document_number: p.cpf,
                        gender:        p.gender || '',
                      }
                      return updated
                    })
                    if (p.email && !contactEmail) setContactEmail(p.email)
                    if (p.phone && !contactPhone) setContactPhone(p.phone)
                    if (!p.cpf || !p.birth_date) setShowErrors(true)
                  }} />

                  {showNewPassenger && (
                    <div style={{ border: '1px solid #e2e8f0', borderRadius: '8px', padding: '14px', marginBottom: '12px', background: '#f8fafc' }}>
                      <div style={{ fontSize: '11px', fontWeight: 700, color: colors.primary, marginBottom: '10px', textTransform: 'uppercase' }}>Cadastrar Novo Passageiro</div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '8px' }}>
                        {[['Nome', 'first_name'], ['Sobrenome', 'last_name'], ['Nascimento', 'birth_date'], ['CPF', 'cpf'], ['E-mail', 'email'], ['Telefone', 'phone']].map(([label, field]) => (
                          <div key={field}>
                            <div style={labelStyle}>{label}</div>
                            <input type={field === 'birth_date' ? 'date' : 'text'} style={inputStyle} value={newPassenger[field] || ''} onChange={e => setNewPassenger(prev => ({ ...prev, [field]: e.target.value }))} />
                          </div>
                        ))}
                        <div>
                          <div style={labelStyle}>Sexo</div>
                          <select style={inputStyle} value={newPassenger.gender || ''} onChange={e => setNewPassenger(prev => ({ ...prev, gender: e.target.value }))}>
                            <option value="">—</option><option value="M">Masculino</option><option value="F">Feminino</option>
                          </select>
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                        <button onClick={() => { setShowNewPassenger(false); setNewPassenger({}) }} style={{ padding: '7px 14px', border: '1px solid #e2e8f0', borderRadius: '6px', background: 'white', fontSize: '12px', cursor: 'pointer' }}>Cancelar</button>
                        <button disabled={savingPassenger} onClick={async () => {
                          if (!newPassenger.first_name || !newPassenger.last_name || !newPassenger.birth_date || !newPassenger.cpf) { alert('Nome, sobrenome, nascimento e CPF são obrigatórios'); return }
                          setSavingPassenger(true)
                          try {
                            const r = await fetch(`${API_URL}/passengers`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(newPassenger) })
                            const d = await r.json()
                            if (r.ok) {
                              const p = d.passenger
                              setPassengers(prev => { const updated = [...prev]; const idx = updated.findIndex(x => !x.first_name); updated[idx >= 0 ? idx : 0] = { ...updated[idx >= 0 ? idx : 0], _passenger_id: p.id, first_name: p.first_name, last_name: p.last_name, birth_date: p.birth_date, document_number: p.cpf, gender: p.gender || '' }; return updated })
                              setShowNewPassenger(false); setNewPassenger({})
                            } else { alert(d.error || 'Erro ao salvar') }
                          } catch { alert('Erro de conexão') }
                          setSavingPassenger(false)
                        }} style={{ padding: '7px 14px', border: 'none', borderRadius: '6px', background: colors.primary, color: 'white', fontSize: '12px', fontWeight: 700, cursor: savingPassenger ? 'not-allowed' : 'pointer' }}>
                          {savingPassenger ? 'Salvando...' : 'Salvar e Usar'}
                        </button>
                      </div>
                    </div>
                  )}

                  {passengers.map((p, i) => (
                    <PassengerForm key={i} index={i} type={p.type} data={p} onChange={handlePassengerChange} showErrors={showErrors} />
                  ))}
                </div>

                {/* Contato */}
                <div style={{ marginBottom: '20px' }}>
                  <div style={{ fontSize: '12px', fontWeight: 700, color: '#0f172a', marginBottom: '12px' }}>Contato</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                    <div>
                      <div style={labelStyle}>E-mail *</div>
                      <input style={showErrors && !contactEmail ? inputErrorStyle : inputStyle} type="email" placeholder="email@exemplo.com" value={contactEmail} onChange={e => setContactEmail(e.target.value)} />
                    </div>
                    <div>
                      <div style={labelStyle}>Telefone *</div>
                      <input style={showErrors && !contactPhone ? inputErrorStyle : inputStyle} placeholder="(77) 99999-9999" value={contactPhone} onChange={e => setContactPhone(e.target.value)} />
                    </div>
                  </div>
                </div>

                {/* Pagamento — escondido para cliente público */}
                {!isPublic && (
                <div style={{ marginBottom: '20px' }}>
                  <div style={{ fontSize: '12px', fontWeight: 700, color: '#0f172a', marginBottom: '12px' }}>Pagamento</div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    {['pix', 'credit_card', 'boleto'].map(m => (
                      <button key={m} onClick={() => setPaymentMethod(m)} style={{
                        flex: 1, padding: '10px 8px',
                        border: `2px solid ${paymentMethod === m ? colors.accent : '#e2e8f0'}`,
                        borderRadius: '8px', background: paymentMethod === m ? 'rgba(80,207,173,0.06)' : 'white',
                        cursor: 'pointer', fontSize: '12px', fontWeight: 600,
                        color: paymentMethod === m ? colors.primary : '#64748b'
                      }}>
                        {m === 'pix' ? 'PIX' : m === 'credit_card' ? 'Cartão' : 'Boleto'}
                      </button>
                    ))}
                  </div>
                </div>
                )}

                {/* Erro */}
                {error && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '10px 12px', marginBottom: '16px', color: '#dc2626', fontSize: '12px' }}>
                    <AlertCircle size={14} /> {error}
                  </div>
                )}

                {/* Botões */}
                <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                  <button onClick={onClose} style={{ padding: '10px 18px', border: '1px solid #e2e8f0', borderRadius: '8px', background: 'white', color: '#64748b', fontSize: '13px', cursor: 'pointer' }}>
                    Cancelar
                  </button>
                  <button onClick={handleReservar} disabled={loading} style={{
                    padding: '10px 22px', border: 'none', borderRadius: '8px',
                    background: loading ? '#e2e8f0' : colors.primary,
                    color: loading ? '#94a3b8' : 'white',
                    fontSize: '13px', fontWeight: 700,
                    cursor: loading ? 'not-allowed' : 'pointer',
                    display: 'flex', alignItems: 'center', gap: '6px'
                  }}>
                    {loading
                      ? <><Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} /> Reservando...</>
                      : <><CreditCard size={13} /> Confirmar Reserva</>
                    }
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {showDetail && bookingResult && (
        <BookingDetail
          booking={{
            ...bookingResult.booking,
            payment_method: bookingResult.payment?.method,
            pix_code: bookingResult.payment?.pix_code,
            pix_qr_code: bookingResult.payment?.pix_qr_code,
            pix_expires_at: bookingResult.payment?.expires_at,
            outbound,
            return_flight: returnFlight,
            passengers,
            origin, destination,
            departure_date: departureDate,
            return_date: returnDate,
            contact_email: contactEmail,
            contact_phone: contactPhone,
          }}
          onClose={() => setShowDetail(false)}
        />
      )}
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </>
  )
}
